import { Card, Row, Col, ProgressBar, Badge } from 'react-bootstrap';
import { FaBolt, FaBroadcastTower, FaTint, FaThermometerHalf } from 'react-icons/fa';
import { BsCloudArrowUpFill } from "react-icons/bs";
import './SiloBagDetalhado.css';

const SiloBagDetalhado = ({ battery, signal, voltage, rssi, onPointClick, pontoSelecionado, dadosSiloBag }) => {
  // Função helper para tratar valor -88 como N/A
  const formatValue = (value, decimals = 1, unit = '') => {
    if (value === null || value === undefined || value === -88) {
      return 'N/A';
    }
    if (typeof value === 'number') {
      return decimals > 0 ? `${value.toFixed(decimals)}${unit}` : `${value}${unit}`;
    }
    return 'N/A';
  };

  // Função para determinar qualidade do sinal LoRa baseado no RSSI (tabela final)
  const getSignalQuality = (rssiValue) => {
    // RSSI = 0 significa o melhor sinal possível
    if (rssiValue === 0 || rssiValue >= -40) return 'Excelente';
    if (rssiValue >= -60) return 'Ótimo';
    if (rssiValue >= -80) return 'Normal';
    if (rssiValue >= -100) return 'Regular';
    return 'Fraco';
  };

  // Função para determinar cor e status do CO2
  const getCO2Status = (co2Value) => {
    if (co2Value >= 10) {
      return {
        color: '#dc3545', // Vermelho
        variant: 'danger',
        status: 'perigo',
        showFermentation: true,
        bgClass: 'co2-danger'
      };
    } else if (co2Value >= 7.5) {
      return {
        color: '#ffc107', // Amarelo
        variant: 'warning', 
        status: 'alerta',
        showFermentation: false,
        bgClass: 'co2-warning'
      };
    } else {
      return {
        color: '#6c757d', // Cinza para valores baixos
        variant: 'secondary',
        status: 'baixo',
        showFermentation: false,
        bgClass: 'co2-low'
      };
    }
  };

  return (
    <Card className="silo-bag-detalhado-card bg-dark text-light">
      <Card.Body>
        <div className="silo-details-header">
          <Row className="align-items-center mb-3">
            <Col xs={8}>
              <div className="d-flex align-items-center">
                <h5 className="mb-0 me-2">Ponto de Leitura:</h5>
                <span className="ponto-leitura-numero">
                  {pontoSelecionado && typeof pontoSelecionado.time === 'string' ? pontoSelecionado.time.replace('Ponto ', '') : 'N/A'}
                </span>
              </div>
            </Col>
            <Col xs={4} className="text-end">
                <button className="btn btn-sm btn-outline-secondary" onClick={() => onPointClick(null)}>
                    Fechar Detalhes
                </button>
            </Col>
          </Row>
        </div>

        <Row className="g-3">
            {/* Coluna da Esquerda: Imagem do Silo */}
            <Col lg={5} className="d-flex justify-content-center align-items-center flex-column">
                <div className="silo-image-container">
                    <img src="/images/silobag.png" alt="Silo Bag" className="silo-image" />
                    {Array.isArray(dadosSiloBag) && dadosSiloBag.map((ponto, index) => {
                        // Calcula a posição dinamicamente baseado no número total de pontos
                        const totalPontos = dadosSiloBag.length;
                        const margemTopo = 8; // Margem do topo em %
                        const margemFundo = 8; // Margem do fundo em %
                        const espacoDisponivel = 100 - margemTopo - margemFundo;
                        const espacamento = totalPontos > 1 ? espacoDisponivel / (totalPontos - 1) : 0;
                        const posicao = margemTopo + (index * espacamento);
                        
                        // Determina a classe de cor baseado no CO2 e se tem dados
                        const hasData = ponto.hasData !== false; // Verifica se o ponto tem dados
                        const co2Value = ponto.co2 === -88 ? 0 : ponto.co2;
                        let co2Class = '';
                        
                        if (!hasData) {
                            co2Class = 'no-data'; // Cinza para pontos sem dados
                        } else if (co2Value >= 10) {
                            co2Class = 'alert-co2-danger'; // Vermelho
                        } else if (co2Value >= 7.5) {
                            co2Class = 'alert-co2-warning'; // Amarelo
                        }
                        // Se < 7.5%, não adiciona classe (fica cinza/verde padrão)
                        
                        return (
                            <div
                                key={index}
                                className={`silo-point-marker ${pontoSelecionado && pontoSelecionado.time === ponto.time ? 'active' : ''} ${co2Class}`}
                                style={{ top: `${posicao}%` }}
                                onClick={() => hasData ? onPointClick(ponto) : null}
                                title={hasData ? `Ponto ${index + 1}` : `Ponto ${index + 1} - Sem dados`}
                            >
                                {index + 1}
                            </div>
                        );
                    })}
                </div>
                <p className="text-muted text-center mt-3">Clique em outro ponto no silo para atualizar os valores.</p>
            </Col>

            {/* Coluna da Direita: Blocos dos Valores */}
            <Col lg={7}>
                <div className="metrics-column">
                    {/* Umidade */}
                    <div className="metric-card-wrapper umidade">
                        <div className="metric-card-header">
                            <div className="metric-card-icon"><FaTint /></div>
                            <div className="metric-card-info">
                                <div className="d-flex align-items-center justify-content-between w-100">
                                    <span className="metric-card-label">UMIDADE</span>
                                    <span className="metric-card-value">{formatValue(pontoSelecionado?.umidade, 1, '%')}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Temperatura Ambiente */}
                    <div className="metric-card-wrapper temperatura">
                        <div className="metric-card-header">
                            <div className="metric-card-icon"><FaThermometerHalf /></div>
                            <div className="metric-card-info">
                                <div className="d-flex align-items-center justify-content-between w-100">
                                    <span className="metric-card-label">TEMP. AMBIENTE</span>
                                    <span className="metric-card-value">{formatValue(pontoSelecionado?.temperatureAmbient, 1, '°C')}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Temperatura CO2 */}
                    <div className="metric-card-wrapper temperatura">
                        <div className="metric-card-header">
                            <div className="metric-card-icon"><FaThermometerHalf /></div>
                            <div className="metric-card-info">
                                <div className="d-flex align-items-center justify-content-between w-100">
                                    <span className="metric-card-label">TEMP. CO₂</span>
                                    <span className="metric-card-value">{formatValue(pontoSelecionado?.temperatureCO2, 1, '°C')}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CO2 com barra de progresso */}
                    {(() => {
                        // CO2 agora vem em porcentagem (0-100%)
                        const rawCO2 = pontoSelecionado?.co2;
                        const isInvalid = rawCO2 === null || rawCO2 === undefined || rawCO2 === -88;
                        const co2Value = isInvalid ? 0 : rawCO2;
                        const co2Status = getCO2Status(co2Value);
                        
                        // Calcula a porcentagem para a barra (máximo 25% = 100% da barra)
                        const barPercentage = Math.min((co2Value / 25) * 100, 100);
                        
                        // Determina a variante da barra baseado no status
                        let barVariant = 'secondary';
                        if (co2Value >= 10) {
                            barVariant = 'danger';
                        } else if (co2Value >= 7.5) {
                            barVariant = 'warning';
                        }
                        
                        return (
                            <div className={`metric-card-wrapper co2 ${co2Status.bgClass}`}>
                                <div className="metric-card-header">
                                    <div className="metric-card-icon"><BsCloudArrowUpFill /></div>
                                    <div className="metric-card-info">
                                        <div className="d-flex align-items-center justify-content-between w-100">
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="metric-card-label">CO₂</span>
                                                {co2Status.showFermentation && !isInvalid && (
                                                    <Badge bg="danger" className="fermentation-badge">
                                                        🧪 FERMENTAÇÃO
                                                    </Badge>
                                                )}
                                            </div>
                                            <span className="metric-card-value">
                                                {isInvalid ? 'N/A' : `${co2Value.toFixed(2)}%`}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <ProgressBar 
                                    now={isInvalid ? 0 : barPercentage} 
                                    variant={barVariant}
                                />
                                <small className="metric-card-ideal">
                                    {isInvalid ? 'Sem dados' : `${(co2Value * 10000).toFixed(0)} ppm`}
                                </small>
                            </div>
                        );
                    })()}

                    {/* Bateria */}
                    <div className="metric-card-wrapper bateria">
                        <div className="metric-card-header">
                            <div className="metric-card-icon"><FaBolt /></div>
                            <div className="metric-card-info">
                                <div className="d-flex align-items-center justify-content-between w-100">
                                    <span className="metric-card-label">BATERIA</span>
                                    <span className="metric-card-value">
                                        {voltage !== undefined && voltage !== null && voltage > 0 ? `${battery}%` : 'Sem dados'}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <ProgressBar 
                            now={voltage !== undefined && voltage !== null && voltage > 0 ? battery : 0} 
                            variant="success"
                        />
                        <small className="metric-card-ideal">
                            {voltage !== undefined && voltage !== null && voltage > 0 ? `${voltage.toFixed(2)}V` : 'Sem dados'}
                        </small>
                    </div>

                    {/* Sinal LoRa */}
                    <div className="metric-card-wrapper sinal">
                        <div className="metric-card-header">
                            <div className="metric-card-icon"><FaBroadcastTower /></div>
                            <div className="metric-card-info">
                                <div className="d-flex align-items-center justify-content-between w-100">
                                    <span className="metric-card-label">SINAL LORA</span>
                                    <span className="metric-card-value">
                                        {rssi !== undefined && rssi !== null ? getSignalQuality(rssi) : 'Sem dados'}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <ProgressBar 
                            now={rssi !== undefined && rssi !== null ? signal : 0} 
                            variant={signal > 70 ? 'success' : signal > 40 ? 'warning' : 'danger'} 
                        />
                        <small className="metric-card-ideal">
                            {rssi !== undefined && rssi !== null ? `${signal}% (${rssi}dBm)` : 'Sem dados'}
                        </small>
                    </div>
                </div>
            </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default SiloBagDetalhado;
